import React, { useState } from 'react';
import {buildClickTrackingUrl, decodeString, readableReadingTime} from '../_Global/searchHelpers';
import {renderHTML} from '../_Global/searchReactHelpers';
import useSearchStore from '../_Global/SearchStore';

import { DateTime, Settings } from "luxon";

interface ResultNewsConfiguration {
    result: SearchResult;
}

/**
 * Configure the Luxon time zone so that all
 * instances of DateTime use the "correct" local time.
 * Timezone from: https://www.iana.org/time-zones
 *
 * @source https://moment.github.io/luxon/#/zones?id=changing-the-default-zone
 */
Settings.defaultZone = "Australia/Sydney";

function ResultNews({result}: ResultNewsConfiguration) {
    const hasImage = !!result?.listMetadata?.imageUrl ?? false;
    const prettyDate = DateTime.fromISO(result?.listMetadata?.d ?? '').toFormat('dd LLL y');
    const searchTermHighlighting = useSearchStore((state) => state.searchTermHighlighting);

    /**
     * Return the result summary based on the application configuration
     * and the order of preference for the content, as set here.
     */
    function getResultSummaryContent() {
      let searchSummaryContent = decodeString(result['summary']);

      if (searchTermHighlighting) {
        searchSummaryContent = result['summaryHighlighted'] || '';
      }

      if (!searchTermHighlighting && result['summaryTruncated']) {
        searchSummaryContent = result['summaryTruncated'];
      }

      return searchSummaryContent;
    }

    const handleClick = (e) => {
        const clicktrackingValue = e.target.getAttribute("data-clicktracking");
        const dataHref = e && e.target && e.target.getAttribute("data-href")?e.target.getAttribute("data-href"):null;
        if (dataHref !== null && dataHref!== undefined && dataHref !== '') {
            fetch(clicktrackingValue)
              .then((response) => response.json())
              .then((data) => {
                console.log("Tracking:", clicktrackingValue);
              })
              .catch((error) => console.error("Error fetching data:", error));
            const timer = setTimeout(() => {
                window.location.assign(dataHref);
            }, 200);
        }
    }

    return (
        <div key={result['rank']} className={`col-span-4 xs:col-span-6 md:col-span-4 lg:col-span-3 card${hasImage ? ' card--image' : ''} no-wysiwyg`}>
            <a onClick={handleClick} data-clicktracking={buildClickTrackingUrl(result['clickTrackingUrl'])} data-href={result['listMetadata']['url']}>
                <div className="card__copy">
                    <div className="card__top">
                        <div className="card-tag body-caption">
                            <svg className="card-tag__svg">
                                <use xlinkHref="#square"></use>
                            </svg>
                            <span className="card-tag__text">{result['listMetadata']['type']}</span>
                        </div>

                        <h3 className="card__heading">{result['title']}</h3>
                    </div>
                    <div className="card__bottom">
                        {hasImage && (
                            <div
                                className="card__image"
                                style={{
                                    backgroundImage: `url('${result['listMetadata']['imageUrl']}')`,
                                }}
                            ></div>
                        )}
                        { renderHTML('p', 'card__text body-size-lg', getResultSummaryContent()) }
                        <div className="card__metadata metadata-wrapper metadata-wrapper--inline">
                            <div className="metadata-block metadata-block--inline">
                                <svg className="metadata-block__svg body-size-lg">
                                    <use xlinkHref="#calendar"></use>
                                </svg>
                                <span className="metadata-block__value body-size-lg">
                                    {prettyDate}
                                </span>
                            </div>
                            <span className="vertical-divider" role="presentation"></span>
                            <div className="metadata-block metadata-block--inline">
                                <span className="metadata-block__value body-size-lg">{readableReadingTime(result['listMetadata']['readingTime'])}</span>
                            </div>
                        </div>
                        <div className="card__arrow-link">
                            <svg role="graphics-symbol">
                                <use xlinkHref="#arrow-diagonal"></use>
                            </svg>
                        </div>
                    </div>
                </div>
            </a>
        </div>
    );
}

/**
 * Create the markup for this people result.
 */
export default React.memo(ResultNews);
